<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <TitleCard
        :is-show-mode="isShowMode"
        :is-process-mode="isProcessMode"
        :is-edit-mode="isEditMode"
        :disabled="invalid"
        :saveLoading="saveBtnLoading"
        :selected-item="educationalPublication"
        :is-no-changes="isNoChanges"
        @save="saveEducationalPublication"
      />

      <v-row
        class="mb-0 mt-2"
        style="margin-right: -14px"
      >
        <v-col
          ref="workList"
          cols="12"
          class="pt-1 pb-2 pr-2 overflow-auto"
        >
          <WorkTitleCard :title="workTypeRusName(workTypeName.EducationalPublication)" />

          <AuthorsCard
            class="mt-4"
            title="Автор"
            :agreementFileList="agreementFileList"
            :is-foreign-publication="educationalPublication.isForeign"
            :selected-item.sync="educationalPublication.commonWork.authors"
            :is-show-mode="isShowMode"
            :upload-files="true"
            @updateInvalid="updateAuthorsInvalid"
            @uploadAgreementFile="initUploadAgreementFile"
            @removeAgreementFile="initRemoveAgreementFile"
          >
            <CardRow>
              <v-checkbox
                class="rounded-lg mini-text field-edit d-inline-block"
                v-model="educationalPublication.isForeign"
                label="Публикация на иностранном языке"
                hide-details
                dense
                :disabled="isShowMode"
              />
            </CardRow>
          </AuthorsCard>

          <EducationalPublicationCard
            class="mt-4"
            title="Основная информация"
            :linkFileList="linkFileList"
            :selected-item.sync="educationalPublication"
            :is-show-mode="isShowMode"
            @updateInvalid="updateEducationalPublicationInvalid"
            @uploadLinkFiles="initUploadLinkFiles"
            @removeLinkFile="initRemoveLinkFile"
          />

          <CommentCard
            v-if="Boolean(educationalPublication.commonWork.comment)"
            class="mt-4"
            :comment="educationalPublication.commonWork.comment"
          />

          <CreatorCard :creator-name="educationalPublication.commonWork.creator.fullName" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { educationalPublicationApi } from '@/api'
import TitleCard from '../../components/Works/elements/TitleCard'
import { EducationalPublication } from '../../models'
import EducationalPublicationCard from '../../components/Works/EducationalPublication/EducationalPublicationCard'
import AuthorsCard from '../../components/Works/elements/AuthorsCard'
import CommentCard from '@/components/Works/elements/CommentCard.vue'
import { mapActions, mapGetters } from 'vuex'
import { workListHeight } from '@/mixins/workListHeight'
import { dictionaryNames, fileTypeId, workMode, workTypeName } from '@/scripts/consts'
import WorkTitleCard from '@/components/Works/elements/WorkTitleCard.vue'
import CreatorCard from '@/components/Works/elements/CreatorCard.vue'
import CardRow from '@/components/ui/row/CardRow.vue'
import { filesForWork } from '@/mixins/filesForWork'

export default {
  name: 'EducationalPublicationParent',

  metaInfo() {
    return {
      title: this && this.workTypeRusName(workTypeName.EducationalPublication)
    }
  },

  components: {
    CardRow,
    CreatorCard,
    WorkTitleCard,
    CommentCard,
    AuthorsCard,
    EducationalPublicationCard,
    TitleCard
  },

  mixins: [workListHeight, filesForWork],

  created() {
    this.isShowMode = this.$route.params.mode === workMode.Show
    this.isProcessMode = this.$route.params.mode === workMode.Process
    this.isEditMode = this.$route.params.mode === workMode.Edit
    this.getData()
  },

  data: () => ({
    educationalPublication: new EducationalPublication(),
    selectedItem: null,
    isShowMode: false,
    isEditMode: false,
    isProcessMode: false,
    saveBtnLoading: false,
    dataLoaded: false,
    authorsInvalid: false,
    educationalPublicationInvalid: false,
    isNoChanges: true
  }),

  computed: {
    workTypeName() {
      return workTypeName
    },
    ...mapGetters('workTypes', ['workTypeByName', 'workTypeRusName']),

    invalid() {
      return this.authorsInvalid || this.educationalPublicationInvalid
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    ...mapActions('workTypes', ['getWorkTypes']),

    async getData() {
      if (!this.isShowMode)
        await this.getDictionaries([
          dictionaryNames.Year,
          dictionaryNames.PublicationForm,
          dictionaryNames.PublishingHouse,
          dictionaryNames.Highschool,
          dictionaryNames.EducationalPublicationType
        ])
      await this.getWorkTypes()
      await this.getEducationalPublication()
      this.dataLoaded = true
      this.isNoChanges = true
    },

    async getEducationalPublication() {
      try {
        if (this.$route.params?.id !== '0') {
          const data = await educationalPublicationApi.get(this.$route.params?.id)
          await this.getFileList(data.commonWork?.id)
          this.educationalPublication = new EducationalPublication(data)
        }
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить данные по работе')
      }
    },

    async saveEducationalPublication(isThrowError = false) {
      try {
        this.saveBtnLoading = true

        if (!this.educationalPublication.id)
          this.educationalPublication.commonWork.workType = this.workTypeByName(workTypeName.EducationalPublication)

        const body = this.educationalPublication.getForAPI()

        const data = this.educationalPublication.id
          ? await educationalPublicationApi.update(body)
          : await educationalPublicationApi.create(body)

        await this.removeFileFromAPI()

        const linkFileListForSave = this.linkFileList.filter(file => !file.id)
        const agreementFileListForSave = this.agreementFileList.filter(file => !file.id)

        if (linkFileListForSave.length) await this.saveFile(data?.commonWork?.id, linkFileListForSave, fileTypeId.link)
        if (agreementFileListForSave.length)
          await this.saveFile(data?.commonWork?.id, agreementFileListForSave, fileTypeId.agreement)

        if (!this.educationalPublication.id) await this.$router.replace('/works/educationalPublication/edit/' + data.id)

        await this.getFileList(data.commonWork?.id)
        this.educationalPublication = new EducationalPublication(data)
        this.$toast.info('Работа успешно сохранена')
        queueMicrotask(() => (this.isNoChanges = true))
      } catch (e) {
        console.log(e)
        if (e.message === 'Ошибка удаления файлов') this.getFileList(this.educationalPublication.commonWork?.id)
        else this.showErrorMessage(e, 'Ошибка сохранения работы')
        if (isThrowError) throw new Error('Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    initUploadLinkFiles(files) {
      this.uploadLinkFiles(files)
      this.isNoChanges = false
    },

    initRemoveLinkFile(index) {
      this.removeLinkFile(index)
      this.isNoChanges = false
    },

    initUploadAgreementFile(file) {
      this.uploadAgreementFile(file)
      this.isNoChanges = false
    },

    initRemoveAgreementFile(index) {
      this.removeAgreementFile(index)
      this.isNoChanges = false
    },

    async updateAuthorsInvalid(val) {
      this.authorsInvalid = val
      this.isNoChanges = false
    },

    async updateEducationalPublicationInvalid(val) {
      this.educationalPublicationInvalid = val
      this.isNoChanges = false
    }
  },

  watch: {
    dataLoaded() {
      this.setWorkListHeight()
    }
  },

  provide: function () {
    return {
      saveWork: this.saveEducationalPublication
    }
  }
}
</script>

<style scoped></style>
